<template>
  <StudioPlusReactivationModal
    :isOpen="isReactivationModalOpen && cancelled"
    :isLoading="isLoading"
    :defaultPaymentMethod="defaultPaymentMethod"
    :nextPaymentDate="studioPlusCampaign?.nextPaymentDate"
    :isActivationDateNotToday="isActivationDateNotToday"
    :price="studioPlusPrice"
    :isPriceLoading="isPriceLoading"
    :isPriceFetching="isPriceFetching"
    :type="studioPlusCampaign?.term ?? 'YEARLY'"
    :cardExpiresSoon="cardExpiresSoon"
    :cardExpirationDate="cardExpirationDate"
    :isUpdating="isUpdating"
    @reactivate="reactivate"
    @cancel="cancelAction"
    @reset="resetModal"
    @applyVoucherCode="applyVoucherCode"
  />
</template>
<script setup>
import { ref, computed } from 'vue'

import StudioPlusReactivationModal from '@/components/billing/premiumProducts/reactivation/StudioPlusReactivationModal.vue'
import useToastNotifications from '@/components/notifications/useToastNotifications'

import appAnalytics from '@/utils/tracking'
import { useAccommodationsByOrgId, useUserDetails } from '@/layouts/queries'
import {
  useExistingPaymentMethods,
  useZuoraAccount,
  useStudioPlusCampaign,
  useStudioPlusPrice,
  useStudioPlusNewSubscriptionMutation,
} from '@/components/payment/queries'
import { countries } from '@/constants/countries'
import { useStudioPlusReactivation } from '@/components/billing/premiumProducts/reactivation/useStudioPlusReactivation'
import { useSessionValues } from '@/composables/useSessionValues'
import { useFormatting } from '@/composables/useFormatting'

const props = defineProps({
  cancelled: Boolean,
  expired: Boolean,
})

const { isReactivationModalOpen, closeReactivationModal, reactivationAccommodationId, reactivationSource } =
  useStudioPlusReactivation()
const { displayNotification, showSaveError } = useToastNotifications()
const { language, userId, currency, selectedOrganisationId } = useSessionValues()
const { formatDate } = useFormatting()

const isUpdating = ref(false)
const voucherCode = ref('')
const applyVoucherCode = code => {
  voucherCode.value = code
}
const voucherCodeValue = computed(() => voucherCode.value)

const { existingPaymentMethods, existingPaymentMethodsLoading } = useExistingPaymentMethods(reactivationAccommodationId)
const defaultPaymentMethod = computed(() => existingPaymentMethods.value?.find(item => item.isDefault))

const { studioPlusCampaign, isLoading: studioPlusCampaignIsLoading } =
  useStudioPlusCampaign(reactivationAccommodationId)

const { userDetails } = useUserDetails(userId)
const { sendStudioPlusNewSubscription } = useStudioPlusNewSubscriptionMutation()
const { accommodationsData } = useAccommodationsByOrgId(selectedOrganisationId)

const accommodationName = computed(
  () =>
    accommodationsData.value.find(item => Number(item.accommodationId) === Number(reactivationAccommodationId.value))
      ?.name
)

const { zuoraAccountData, isLoading: isZuoraDataLoading } = useZuoraAccount()
const countryCode = computed(
  () => countries.find(country => country.name === zuoraAccountData.value?.billToContact?.country)?.code
)

const {
  studioPlusPrice,
  isLoading: isPriceLoading,
  isFetching: isPriceFetching,
} = useStudioPlusPrice({
  selectedAccommodationId: reactivationAccommodationId,
  currency,
  countryCode,
  postalCode: computed(() => zuoraAccountData.value?.billToContact?.zipCode),
  countryState: computed(() => zuoraAccountData.value?.billToContact?.state),
  taxId: computed(() => zuoraAccountData.value?.taxInfo?.exemptCertificateId),
  voucherCode: voucherCodeValue,
  enabled: computed(() => !isZuoraDataLoading.value && !!zuoraAccountData.value),
})

const isLoading = computed(
  () =>
    (isZuoraDataLoading.value && !zuoraAccountData.value) ||
    existingPaymentMethodsLoading.value ||
    (studioPlusCampaignIsLoading.value && !studioPlusCampaign.value)
)

const cardExpirationDate = computed(() => {
  const paymentMethod = defaultPaymentMethod.value
  if (!paymentMethod?.expirationMonth || !paymentMethod?.expirationYear) {
    return null
  }
  return new Date(paymentMethod.expirationYear, paymentMethod.expirationMonth, 0)
})

const cardExpiresSoon = computed(() => {
  if (!cardExpirationDate.value) {
    return false
  }
  const today = new Date()
  const msPerDay = 24 * 60 * 60 * 1000
  const daysUntilExpiration = Math.ceil((cardExpirationDate.value.getTime() - today.getTime()) / msPerDay)
  return daysUntilExpiration < 30 && daysUntilExpiration >= 0
})

const isActivationDateNotToday = computed(() => {
  const nextPaymentDate = studioPlusCampaign.value?.nextPaymentDate
  if (!nextPaymentDate) return false
  const today = new Date()
  const activation = new Date(nextPaymentDate)
  return (
    activation.getFullYear() !== today.getFullYear() ||
    activation.getMonth() !== today.getMonth() ||
    activation.getDate() !== today.getDate()
  )
})

const reactivate = () => {
  isUpdating.value = true
  sendStudioPlusNewSubscription(
    {
      accommodationId: reactivationAccommodationId.value,
      itemName: accommodationName.value,
      email: userDetails?.value?.email,
      language: language?.value,
      studioUserId: userId.value,
      firstName: userDetails?.value?.firstName,
      lastName: userDetails?.value?.lastName,
      ...(studioPlusPrice?.value?.itemPriceVoucher?.isValid && {
        voucherCode: studioPlusPrice?.value?.itemPriceVoucher?.voucherCode,
      }),
    },
    {
      onSuccess: data => {
        isUpdating.value = false
        appAnalytics.track(appAnalytics.events.SP_SUBSCRIPTION_REACTIVATED, {
          item_id: reactivationAccommodationId.value,
          reactivation_source: reactivationSource.value,
          language: language.value,
          studioUserId: userId.value,
          email: userDetails?.value?.email,
          firstName: userDetails?.value?.firstName,
          lastName: userDetails?.value?.lastName,
          price: studioPlusPrice?.value,
          reactivation_date: formatDate(new Date(), {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          }),
          subscription_type: studioPlusCampaign.value?.term,
          previous_product_type: studioPlusCampaign.value?.type ?? 'PLUS',
          auto_renewal_date: formatDate(data?.data?.nextPaymentDate, {
            year: 'numeric',
            month: 'long',
            day: '2-digit',
          }),
        })
        displayNotification({
          title: 'setting_updated_success',
          titleTranslationKey: true,
          message: 'rc_campaign_resumed_header',
          isTranslationKey: true,
          type: 'success',
        })
        closeReactivationModal()
      },
      onError: () => {
        isUpdating.value = false
        showSaveError()
        closeReactivationModal()
      },
    }
  )
}

const cancelAction = () => {
  appAnalytics.track(appAnalytics.events.SP_REACTIVATION_CANCEL_BTN, {
    accommodationId: reactivationAccommodationId.value,
    isExpired: props.expired,
  })
  resetModal()
}

const resetModal = () => {
  voucherCode.value = ''
  closeReactivationModal()
}
</script>
